import React, { useCallback, useContext, useEffect, useState } from 'react';

import { navigate, RouteComponentProps } from '@reach/router';

import { GlobalContext } from '../../../store';
import { IEventEnrollment } from '../../../store/admin/eventEnrollments/store';

import { useTabs } from '../../../contrib/Hooks/useTabs.hook';
import { Subheader } from '../../../scenes/admin/Common/Subheader/Subheader';
import { AdminTabsNav, ITabStep } from '../Common/AdminTabsNav/AdminTabsNav';
import BasicInformation from './BasicInformation/BasicInformation';
import EventHistory from './EventHistory';
import { IAdminEvent } from '../../../store/admin/events/store';

interface IProps extends RouteComponentProps {
  clubId: number;
  userId: number;
}

const PlayerInfo = (props: IProps) => {
  const { userId, clubId } = props;

  const {
    actions,
    actions: {
      adminMemberships: { getMemberships },
      adminEvents: { all: getAllAdminEvents }
    },
    adminPlayerEnrollments,
    adminEvents: events,
    adminMemberships: memberships
  } = useContext(GlobalContext);

  const { activeTab, leaveTab, direction, setTab } = useTabs();

  const [userMembershipUserId, setUserMembershipUserId] = useState<number>();
  const [subheaderTitle, setSubheaderTitle] = useState('');
  const [subheaderProfileImage, setSubheaderProfileImage] = useState<string>();
  const [userEventsData, setUserEventsData] = useState<IAdminEvent[]>([]);
  const [steps, setSteps] = useState<ITabStep[]>([]);

  const navigateToRoster = useCallback(() => {
    navigate(`/staff/club/${clubId}/players-roster`);
  }, [clubId]);

  useEffect(() => {
    const userMembership = memberships.memberships.find(
      membership => Number(membership.id) === Number(userId)
    );

    if (userMembership) {
      setUserMembershipUserId(userMembership.user.id);
      setSubheaderTitle(`${userMembership.user.firstName} ${userMembership.user.lastName}`);
      setSubheaderProfileImage(userMembership.user.profile.image);
    }
  }, [memberships, userId]);

  useEffect(() => {
    const fetcher = async () => {
      await getAllAdminEvents(clubId);
    };

    fetcher().catch();
  }, [clubId, userId]);

  useEffect(() => {
    if (userMembershipUserId) {
      const fetcher = async () => {
        await actions.adminPlayerEnrollments.getByUserId(clubId, userMembershipUserId);
      };

      fetcher().catch();
    }
  }, [clubId, userMembershipUserId]);

  useEffect(() => {
    if (clubId) {
      getMemberships(String(clubId)).catch();
    }
  }, [clubId]);

  useEffect(() => {
    if (userMembershipUserId) {
      const fetcher = async () => {
        await actions.adminMemberships.getByUser(clubId, userMembershipUserId);
      };

      fetcher().catch();
    }
  }, [clubId, userMembershipUserId]);

  useEffect(() => {
    setSteps([
      {
        content: <BasicInformation clubId={clubId} userId={userId} />,
        label: 'PLAYER INFO'
      },
      {
        content: <EventHistory userEvents={userEventsData} />,
        label: 'EVENT HISTORY'
      }
    ]);
  }, [clubId, userId, userEventsData]);

  useEffect(() => {
    const allEvents = Object.values(events.all);
    const clubEnrollments: IEventEnrollment[] = adminPlayerEnrollments[clubId] || [];

    if (allEvents.length > 0 && clubEnrollments.length > 0) {
      const eventsWithEnrollments = clubEnrollments.map(enrollment => Number(enrollment.event));

      setUserEventsData(
        allEvents.filter(event => eventsWithEnrollments.includes(Number(event.id)))
      );
    }
  }, [events, events.all, adminPlayerEnrollments, clubId]);

  return (
    <div className="admin-layout">
      <Subheader title={subheaderTitle} profileImage={subheaderProfileImage} />

      <AdminTabsNav
        steps={steps}
        activeTab={activeTab}
        setTab={setTab}
        backButtonText="PLAYER ROSTER"
        backButtonOnClick={navigateToRoster}
      />

      <div className={`slider ${direction}`}>
        {steps.map(({ content, label }, index) => (
          <div
            key={`playerInfo-${label}`}
            className={`slide top ${activeTab === index ? 'active' : ''} ${
              leaveTab === index ? 'leave' : ''
            }`}
          >
            {content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerInfo;
